/**
 * components/SegmentedControl
 * ──────────────────────────────────────────────────────────────────────────
 * The design's segmented control: a #F2F2F2 track with 2px padding and a 6px
 * radius; the active segment is a white (or ink) chip inside it.
 *
 * Two skins, both in the design:
 *   light  white active chip on the grey track — the header's role selector
 *   ink    ink active chip, white label — Settings' Required / Optional toggle
 *
 * Generic over the value union so callers keep their own types.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize } from '../../theme/typography';

export interface Segment<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  segments: readonly Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  skin?: 'light' | 'ink';
}

export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  skin = 'light',
}: SegmentedControlProps<T>) {
  return (
    <View style={styles.track}>
      {segments.map((segment) => {
        const active = segment.value === value;
        const background = active
          ? skin === 'ink'
            ? neutral.ink
            : neutral.surface
          : 'transparent';
        const color = active
          ? skin === 'ink'
            ? neutral.surface
            : neutral.ink
          : neutral.ink2;

        return (
          <Pressable
            key={segment.value}
            onPress={() => onChange(segment.value)}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            style={[styles.segment, { backgroundColor: background }]}
          >
            <Text numberOfLines={1} style={[styles.label, { color }]}>
              {segment.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  track: {
    flexDirection: 'row',
    padding: 2,
    borderRadius: radii.box,
    backgroundColor: neutral.ruleLight,
    alignSelf: 'flex-start',
  },
  segment: {
    paddingVertical: 4,
    paddingHorizontal: 11,
    // One step tighter than the track, so the chip sits inside it cleanly.
    borderRadius: 4,
  },
  label: { ...sans('500'), fontSize: fontSize.label },
});

export default SegmentedControl;
